import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { useEmail, markEmailMentionsRead, revalidateMentionCaches } from './useData';
import api from '../../services/api';
import { formatDate as formatDateGlobal, formatDateTime, formatDateTimeForList } from '../../config/appConfig';
import { API_BASE_URL } from '../../config/apiConfig';
import { Button } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLoading } from '../../contexts/LoadingContext';
import { cn } from '../../lib/utils';
import ClientAutocomplete from './components/ClientAutocomplete';
import EmailBodyView from './components/EmailBodyView';
import {
  EMAIL_MESSAGE_CANVAS_CLASS,
  EMAIL_MESSAGE_PLAIN_TEXT_CLASS,
} from './utils/emailMessageCanvas';
import EmailCommentsPanel from './components/EmailCommentsPanel';
import CollaborationGuideInfo from './components/CollaborationGuideInfo';
import { MailboxLocationBadge } from './components/MailboxLocationBadge';
import { getNonEmbeddedAttachments } from './utils/attachments';
import AttachmentTextExtractionDot from './components/AttachmentTextExtractionDot';
import { emailPartitionAtSearch, emailComposeThreadPath } from './utils/emailPartitionAtQuery';
import {
  exitEmailDetail,
  openComposeInNewTab,
} from './utils/composeNavigation';
import EntityCustomFieldsSection from '../custom-fields/EntityCustomFieldsSection';
import { CUSTOM_FIELD_ENTITY } from '../custom-fields/entityTypes';

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const partitionAtParam = searchParams.get('partitionAt') ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const { showError } = useNotification();
  const { mutate: globalMutate } = useSWRConfig();
  const { data: email, isLoading, mutate } = useEmail(id ? parseInt(id) : 0, partitionAtParam);
  const partitionQuery = useMemo(
    () => emailPartitionAtSearch(partitionAtParam ?? email?.partitionAt),
    [partitionAtParam, email?.partitionAt]
  );

  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [showClientAutocomplete, setShowClientAutocomplete] = useState(false);
  const [loadingAttachmentIndex, setLoadingAttachmentIndex] = useState<number | null>(null);
  const [showHeaders, setShowHeaders] = useState(false);
  const [readStatusDropdownOpen, setReadStatusDropdownOpen] = useState(false);
  const [replyDropdownOpen, setReplyDropdownOpen] = useState(false);
  const commentsSectionRef = useRef<HTMLDivElement>(null);

  const handleCommentActivity = useCallback(() => {
    void mutate(
      (current) =>
        current
          ? {
              ...current,
              hasComments: true,
              commentCount: (current.commentCount ?? 0) + 1,
            }
          : current,
      { revalidate: false },
    );
  }, [mutate]);

  useEffect(() => {
    if (!email || searchParams.get('collaboration') !== '1') return;
    commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const next = new URLSearchParams(searchParams);
    next.delete('collaboration');
    const qs = next.toString();
    navigate(`/emails/${id}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [email, searchParams, id, navigate]);

  useEffect(() => {
    if (!email?.id) return;
    void markEmailMentionsRead(email.id).then(() => {
      void revalidateMentionCaches(globalMutate);
    });
  }, [email?.id, globalMutate]);

  const handleViewAttachment = async (emailId: number, index: number) => {
    setLoadingAttachmentIndex(index);
    try {
      const { data } = await api.get(`/emails/${emailId}/attachments/${index}${partitionQuery}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to open attachment', err);
    } finally {
      setLoadingAttachmentIndex(null);
    }
  };

  const revalidateUnreadCounts = () => {
    globalMutate((key) => typeof key === 'string' && key.startsWith('/emails/unread-counts'), undefined, { revalidate: true });
  };

  const handleMarkAsRead = async () => {
    if (!email || email.isRead) return;
    setIsMarkingRead(true);
    try {
      await api.post(`/emails/${id}/mark-read${partitionQuery}`);
      mutate();
      revalidateUnreadCounts();
    } catch (error) {
      console.error('Error marking as read:', error);
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleMarkAsUnread = async () => {
    if (!email || !email.isRead || !id) return;
    try {
      await api.post(`/emails/${id}/mark-unread${partitionQuery}`);
      mutate();
      revalidateUnreadCounts();
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  };

  // Mark as read when viewing - must be before any early returns
  useEffect(() => {
    if (email && !email.isRead && id) {
      handleMarkAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email?.id, email?.partitionAt, id]);

  const handleToggleStar = async () => {
    if (!email) return;
    try {
      await api.post(`/emails/${id}/star${partitionQuery}`);
      mutate();
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };

  const handleReply = () => {
    setReplyDropdownOpen(false);
    openComposeInNewTab(emailComposeThreadPath(Number(id), 'reply', partitionAtParam ?? email?.partitionAt));
  };

  const handleReplyAll = () => {
    setReplyDropdownOpen(false);
    openComposeInNewTab(emailComposeThreadPath(Number(id), 'reply-all', partitionAtParam ?? email?.partitionAt));
  };

  const handleForward = () => {
    openComposeInNewTab(emailComposeThreadPath(Number(id), 'forward', partitionAtParam ?? email?.partitionAt));
  };

  const nonEmbeddedAttachments = useMemo(
    () => getNonEmbeddedAttachments(email?.attachments),
    [email?.attachments],
  );

  // Drive GlobalLoading when email detail is loading
  const { incrementLoading, decrementLoading } = useLoading();
  useEffect(() => {
    if (!isLoading) return;
    incrementLoading();
    return () => decrementLoading();
  }, [isLoading, incrementLoading, decrementLoading]);

  if (isLoading) {
    return null;
  }

  if (!email) {
    return <div>Email not found</div>;
  }

  // Normalize from/to/cc: API may use .email or .address; handle array/single object/string
  const addrEmail = (a: any) => (a && (a.email ?? a.address ?? (typeof a === 'string' ? a : ''))) || '';
  const addrName = (a: any) => {
    if (!a) return '';
    if (typeof a === 'string') return a;
    return a.name || addrEmail(a) || '';
  };
  const fromRaw = Array.isArray(email.from) ? email.from[0] : email.from;
  const fromEmail = addrEmail(fromRaw);
  const fromName = addrName(fromRaw) || (fromEmail && fromEmail.split('@')[0]) || 'Unknown';
  const senderInitial = fromName[0]?.toUpperCase() || 'E';

  const toList = Array.isArray(email.to) ? email.to : email.to ? [email.to] : [];
  const ccList = Array.isArray(email.cc) ? email.cc : email.cc ? [email.cc] : [];
  const formatAddr = (a: any) => addrName(a) || addrEmail(a) || '—';
  const toDisplay = toList.map(formatAddr).join(', ');
  const ccDisplay = ccList.map(formatAddr).join(', ');

  const mailboxAccountLabel =
    email.emailAccount?.displayName?.trim() ||
    email.emailAccount?.emailAddress?.trim() ||
    `Account #${email.emailAccountId}`;
  const mailboxFolderLabel = (email.folder || 'INBOX').trim() || 'INBOX';

  return (
    <div className={cn('h-full flex flex-col', colors.background)}>
      {/* Header Bar */}
      <div className={cn('border-b px-6 py-4 flex-shrink-0', colors.surface, colors.border)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => exitEmailDetail(navigate, location)}
              className="p-2 rounded-full"
              title="Back to emails"
            >
              <svg className={cn('w-5 h-5', colors.textSecondary)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Button>
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={handleToggleStar}
                className={`p-2 rounded-full ${
                  email.isStarred 
                    ? 'text-yellow-500 hover:bg-yellow-50' 
                    : cn(colors.textMuted, colors.interactiveHover, 'hover:text-yellow-500')
                }`}
                title={email.isStarred ? 'Unstar' : 'Star'}
              >
                <svg className="w-5 h-5" fill={email.isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </Button>
              {!email.isRead && (
                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="relative inline-block">
              <Button
                variant="ghost"
                onClick={() => setReadStatusDropdownOpen((v) => !v)}
                className={cn('p-2 rounded-full', colors.textMuted, colors.interactiveHover)}
                title="Mark as read / unread"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <svg className={cn('w-3.5 h-3.5 ml-0.5 -mr-0.5 opacity-70', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              {readStatusDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setReadStatusDropdownOpen(false)}
                  />
                  <div className={cn('absolute right-0 top-full mt-1 z-20 min-w-[160px] rounded-lg border py-1 shadow-lg', colors.surface, colors.border)}>
                    <button
                      type="button"
                      onClick={() => {
                        setReadStatusDropdownOpen(false);
                        handleMarkAsRead();
                      }}
                      disabled={email.isRead || isMarkingRead}
                      className={cn('w-full text-left px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed', colors.text, 'hover:bg-black/5 dark:hover:bg-white/5')}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Mark as read
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReadStatusDropdownOpen(false);
                        handleMarkAsUnread();
                      }}
                      disabled={!email.isRead}
                      className={cn('w-full text-left px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed', colors.text, 'hover:bg-black/5 dark:hover:bg-white/5')}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Mark as unread
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="relative inline-block">
              <Button
                variant="default"
                onClick={() => setReplyDropdownOpen((v) => !v)}
                className="flex items-center space-x-2"
                aria-expanded={replyDropdownOpen}
                aria-haspopup="true"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                <span>Reply</span>
                <svg className={cn('w-4 h-4', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              {replyDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setReplyDropdownOpen(false)} />
                  <div className={cn('absolute left-0 top-full mt-1 z-20 min-w-[160px] rounded-lg border py-1 shadow-lg', colors.surface, colors.border)}>
                    <button
                      type="button"
                      onClick={handleReply}
                      className={cn('w-full text-left px-3 py-2 text-sm flex items-center gap-2', colors.text, 'hover:bg-black/5 dark:hover:bg-white/5')}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      Reply
                    </button>
                    <button
                      type="button"
                      onClick={handleReplyAll}
                      className={cn('w-full text-left px-3 py-2 text-sm flex items-center gap-2', colors.text, 'hover:bg-black/5 dark:hover:bg-white/5')}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      Reply to All
                    </button>
                  </div>
                </>
              )}
            </div>
            <Button 
              variant="default" 
              onClick={handleForward}
              className="flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              <span>Forward</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Email content + collaboration sidebar */}
      <div className="flex flex-1 min-h-0 flex-col gap-3 px-4 pb-4 pt-2 sm:px-6 lg:flex-row lg:gap-0">
        {/* Left: message details + body */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="w-full flex-shrink-0 pb-1">
          {/* Subject + From/To/Date in compact bar */}
          <div className={cn('rounded-t-lg border border-b-0 px-4 sm:px-6 py-2', colors.surface, colors.border)}>
            <h1 className={cn('text-lg font-semibold truncate pr-2', colors.text)} title={email.subject || '(no subject)'}>
              {email.subject || '(no subject)'}
            </h1>
            <div className={cn('flex flex-wrap items-start justify-between gap-x-4 gap-y-1 mt-1.5 text-sm', colors.textMuted)}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className={cn('h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-medium', colors.text)}>
                    {senderInitial}
                  </span>
                  <span className={cn('font-medium', colors.text)}>{fromName}</span>
                  {fromEmail && <span className="hidden sm:inline">&lt;{fromEmail}&gt;</span>}
                </span>
                <span>to {toDisplay}</span>
                {ccDisplay && <span>cc {ccDisplay}</span>}
              </div>
              <div className="flex flex-col items-end gap-2 text-xs flex-shrink-0 text-right min-w-0 max-w-[min(100%,16rem)] ml-auto sm:ml-0">
                <span className="whitespace-nowrap text-slate-600 dark:text-slate-400">{formatDateTimeForList(email.date)}</span>
                <MailboxLocationBadge accountLabel={mailboxAccountLabel} folderLabel={mailboxFolderLabel} className="max-w-full" />
              </div>
            </div>
            {/* Message headers - collapsible */}
            {email.messageHeaders != null && email.messageHeaders !== '' && (
              <div className={cn('border-t mt-1.5 pt-1.5', colors.border)}>
                <button
                  type="button"
                  onClick={() => setShowHeaders((v) => !v)}
                  className={cn('text-xs font-medium flex items-center gap-1', colors.textMuted, colors.interactiveHover)}
                >
                  {showHeaders ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Hide message headers
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      View message headers
                    </>
                  )}
                </button>
                {showHeaders && (
                  <pre className={cn('mt-1.5 p-3 rounded border text-[11px] whitespace-pre-wrap break-all overflow-x-auto max-h-64 overflow-y-auto font-mono', colors.surfaceHover, colors.border, colors.textSecondary)}>
                    {email.messageHeaders}
                  </pre>
                )}
              </div>
            )}
          </div>

          {/* Links Section - compact single row */}
          {email.linkedClient && (
            <div className={cn('border-x px-4 sm:px-6 py-1.5', 'bg-blue-50 dark:bg-blue-900/20', colors.border)}>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {email.linkedClient && (
                  <span className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 max-w-full min-w-0', 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200')}>
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <Link
                      to={`/clients/${email.linkedClient.clientId}`}
                      className={cn('truncate font-medium', 'text-indigo-800 dark:text-indigo-200 hover:text-indigo-900 dark:hover:text-indigo-100')}
                    >
                      {email.linkedClient.clientName || `Client #${email.linkedClient.clientId}`}
                    </Link>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post(`/emails/${id}/unlink-client${partitionQuery}`);
                          mutate();
                        } catch (error) {
                          console.error('Error unlinking client:', error);
                        }
                      }}
                      className="flex-shrink-0 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-0.5 rounded"
                      title="Unlink client"
                      aria-label="Unlink client"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons for Linking */}
          {!email.linkedClient && (
            <div className={cn('border-x px-4 sm:px-6 py-2', colors.background, colors.border)}>
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowClientAutocomplete(true)}
                  className={cn('text-sm font-medium h-auto p-0', colors.primaryFg)}
                >
                  + Link to organization
                </Button>
              </div>
            </div>
          )}

          {/* Attachments (exclude embedded/inline images shown in body) */}
          {nonEmbeddedAttachments.length > 0 && (
            <div className={cn('border-x border-b px-4 sm:px-6 py-2', colors.surface, colors.border)}>
              <div className="flex items-center space-x-2 mb-1.5">
                <svg className={cn('w-5 h-5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span className={cn('text-sm font-medium', colors.textSecondary)}>
                  {nonEmbeddedAttachments.length} Attachment{nonEmbeddedAttachments.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {nonEmbeddedAttachments.map((att: any, idx: number) => (
                  <span key={idx} className="relative inline-block">
                    <AttachmentTextExtractionDot
                      index={idx}
                      meta={att}
                      emailAttachments={email?.emailAttachments ?? null}
                    />
                    <button
                      type="button"
                      onClick={() => email?.id && handleViewAttachment(email.id, idx)}
                      disabled={loadingAttachmentIndex === idx}
                      className={cn('inline-flex items-center gap-2 px-2.5 py-1.5 rounded border transition-colors text-sm text-left cursor-pointer disabled:opacity-60 disabled:cursor-wait', colors.surfaceHover, colors.border, colors.interactiveHover)}
                      title={`View ${att.filename || `Attachment ${idx + 1}`}`}
                    >
                      {loadingAttachmentIndex === idx ? (
                        <span className="w-4 h-4 flex-shrink-0 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
                      ) : (
                        <svg className={cn('w-4 h-4 flex-shrink-0', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      <span className={cn('truncate max-w-[200px]', colors.text)} title={att.filename || `Attachment ${idx + 1}`}>
                        {att.filename || `Attachment ${idx + 1}`}
                      </span>
                      <span className={cn('text-xs flex-shrink-0', colors.textMuted)}>
                        {(att.size / 1024).toFixed(2)} KB
                      </span>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <EntityCustomFieldsSection
            entityType={CUSTOM_FIELD_ENTITY.EMAIL}
            entityId={email.id}
            className="border-x border-b rounded-b-lg px-4 sm:px-6"
            compact
          />

          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <div className="w-full py-4 min-w-0">
              <div className={cn('px-6 py-4 min-w-0', EMAIL_MESSAGE_CANVAS_CLASS)}>
                {email.bodyHtml ? (
                  <EmailBodyView
                    bodyHtml={email.bodyHtml}
                    emailId={email.id}
                    apiBaseUrl={API_BASE_URL}
                    emailPartitionAt={email.partitionAt}
                  />
                ) : (
                  <pre className={EMAIL_MESSAGE_PLAIN_TEXT_CLASS}>
                    {email.bodyText || '(no content)'}
                  </pre>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: collaboration panel */}
        <aside
          ref={commentsSectionRef}
          className={cn(
            'flex min-h-0 w-full flex-col overflow-hidden rounded-lg border lg:ml-4 lg:w-80 lg:shrink-0 lg:rounded-none lg:border-l lg:border-y-0 lg:border-r-0 xl:w-96',
            colors.surface,
            colors.border,
          )}
        >
          <div className={cn('shrink-0 border-b px-4 py-3', colors.border)}>
            <div className="flex items-start gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <h2 className={cn('text-sm font-semibold', colors.text)}>
                    {t('comments')}
                    {(email.commentCount ?? 0) > 0 && (
                      <span className={cn('ml-1.5 text-sm font-normal', colors.textMuted)}>
                        ({email.commentCount})
                      </span>
                    )}
                  </h2>
                  <CollaborationGuideInfo />
                </div>
                <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{t('commentsMentionHint')}</p>
              </div>
            </div>
          </div>
          <EmailCommentsPanel
            emailId={email.id}
            partitionAt={email.partitionAt}
            layout="drawer"
            onCommentAdded={handleCommentActivity}
          />
        </aside>
      </div>

      {/* Client Autocomplete Modal */}
      <ClientAutocomplete
        isOpen={showClientAutocomplete}
        onClose={() => setShowClientAutocomplete(false)}
        onSelect={async (clientId) => {
          try {
            await api.post(`/emails/${id}/link-client/${clientId}${partitionQuery}`);
            mutate();
          } catch (error) {
            console.error('Error linking client:', error);
            showError('Failed to link client. Please try again.');
          }
        }}
      />
    </div>
  );
};

export default Detail;
